"use client";

import { UserRole } from "@prisma/client";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { FolderIcon } from "@/components/icons/FolderIcon";
import { HomeIcon } from "@/components/icons/HomeIcon";
import { IIconProps } from "@/components/icons/IconInterface";
import { ProfileIcon } from "@/components/icons/ProfileIcon";
import { Link, usePathname } from "@/i18n/routing";
import { cn } from "@/lib/utils";
import { publicRoutes, ROUTES } from "@/routes";

export interface ILinks {
    href: string;
    label: string;
    icon?: React.ComponentType<React.SVGProps<SVGSVGElement> & IIconProps>;
    roles?: UserRole[];
    isAllowed?: boolean;
}

interface INavLinksProps {
    role: UserRole;
}

export const NavLinks = ({ role }: INavLinksProps) => {
    const t = useTranslations("footer");
    const pathname = usePathname();

    const [ clickedLink, setClickedLink ] = useState<string | null>(null);

    const isActive = (path: string) => pathname === path;

    const handleClick = (href: string) => {
        setClickedLink(href);
        setTimeout(() => setClickedLink(null), 500);
    };

    const links: ILinks[] = [
        { href: ROUTES.HOME, label: t("home"), icon: HomeIcon },
        { href: ROUTES.INVOICE_FOLDERS, label: t("folders"), icon: FolderIcon, roles: [ UserRole.UNASSIGNED ], isAllowed: false },
        { href: ROUTES.PROFILE, label: t("profile"), icon: ProfileIcon },
    ];

    const canAccessLink = (link: ILinks) => {
        if (publicRoutes.includes(link.href)) return true;
        if (!link.roles || link.roles.length === 0) return false;

        const roles = link.roles;
        const isAllowed = link.isAllowed;

        const roleMatches = roles.includes(role);

        return isAllowed ? roleMatches : !roleMatches;
    };

    return (
        <>
            {links.map(link => {
                if (!canAccessLink(link)) return null;

                return (
                    <Link
                        key={ link.href }
                        href={ link.href }
                        onClick={ () => handleClick(link.href) }
                        className={ cn(
                            "flex w-full flex-col items-center justify-center rounded-lg p-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                            {
                                "text-primary": isActive(link.href),
                                "animate-pulse-bg": clickedLink === link.href,
                            },
                        ) }
                    >
                        {link.icon ? <>{pathname === link.href ? <link.icon size={ 24 } isFilled /> : <link.icon size={ 24 } />}</> : null}
                        <span className="mt-1 text-xs uppercase">{link.label}</span>
                    </Link>
                );
            })}
        </>
    );
};